// ============================================================================
// property_access: per-property permissions (Full / Limited)
// ============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PropertyAccessLevel = "full" | "limited";

export interface PropertyAccessRow {
  id: string;
  property_id: string;
  grantee_type: "user" | "company";
  grantee_user_id: string | null;
  grantee_company_id: string | null;
  access_level: PropertyAccessLevel;
  created_at: string;
  updated_at: string;
}

export const ACCESS_LEVEL_LABELS: Record<PropertyAccessLevel, string> = {
  full: "Full access",
  limited: "Limited access",
};

const ACCESS_LEVELS: PropertyAccessLevel[] = ["limited", "full"];

export interface PropertyAccessWithDisplay extends PropertyAccessRow {
  display_name?: string | null;
}

export function usePropertyAccess(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["property-access", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_access")
        .select("*")
        .eq("property_id", propertyId!)
        .order("created_at", { ascending: true });

      if (error) throw error;
      const rows = (data ?? []) as PropertyAccessRow[];
      const userIds = [...new Set(rows.map((r) => r.grantee_user_id).filter(Boolean))] as string[];
      const companyIds = [...new Set(rows.map((r) => r.grantee_company_id).filter(Boolean))] as string[];

      let userNames: Record<string, string> = {};
      let companyNames: Record<string, string> = {};
      if (userIds.length) {
        const { data: profiles } = await supabase.from("profiles").select("id, full_name").in("id", userIds);
        userNames = Object.fromEntries((profiles ?? []).map((p) => [p.id, p.full_name ?? ""]));
      }
      if (companyIds.length) {
        const { data: companies } = await supabase.from("cleaning_companies").select("id, company_name").in("id", companyIds);
        companyNames = Object.fromEntries((companies ?? []).map((c) => [c.id, c.company_name ?? ""]));
      }

      return rows.map((r) => ({
        ...r,
        display_name: r.grantee_type === "user" && r.grantee_user_id ? userNames[r.grantee_user_id] ?? null : r.grantee_type === "company" && r.grantee_company_id ? companyNames[r.grantee_company_id] ?? null : null,
      })) as PropertyAccessWithDisplay[];
    },
    enabled: !!propertyId,
  });
}

export function useUpdatePropertyAccess(propertyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, access_level }: { id: string; access_level: PropertyAccessLevel }) => {
      const { data, error } = await supabase
        .from("property_access")
        .update({ access_level, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as PropertyAccessRow;
    },
    onSuccess: (_, __) => {
      if (propertyId) {
        queryClient.invalidateQueries({ queryKey: ["property-access", propertyId] });
      }
    },
  });
}

export { ACCESS_LEVELS };
